import { AppUser } from 'Src/views/App/App';
import axios from 'Src/helpers/api/axiosInstance';
import { Count } from '../../../models/count';
import { fetchAllSearchPages } from './fetchAllSearchPages';
import {
  normalizeSchoolUserContactSearchWhere,
  normalizeSchoolUserSearchFilter
} from './schoolUserContactSearch';

const DEFAULT_SCHOOL_USERS_FILTER: any = {
  'permissions.preset': { $in: ['ADMIN', 'MANAGER', 'TEACHER', 'COACH', 'PARENT', 'STUDENT', 'GOVERNOR'] }
};

/** USER TABLE */
export function getSchoolUsers(user: AppUser, filter = DEFAULT_SCHOOL_USERS_FILTER) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const requestFilter = normalizeSchoolUserSearchFilter(filter);

  const config = {
    headers: { usid: key },
    params: {
      filter: requestFilter
    }
  };

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/users`, config).then(response => {
    return response.data;
  });
}

export function searchSchoolUsers(user: AppUser, filter): Promise<{ items: any[]; total: number }> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/users/search`, normalizeSchoolUserSearchFilter(filter), {
      headers: { usid: key }
    })
    .then(response => response.data);
}

export function getAllSchoolUsers(user: AppUser, where?) {
  return fetchAllSearchPages(filter => searchSchoolUsers(user, filter), {
    ...DEFAULT_SCHOOL_USERS_FILTER,
    ...(where || {})
  });
}

export function getSchoolUsersCount(user: AppUser, where?): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: where
        }
      }
    };
  } else {
    config = {
      headers: { usid: key }
    };
  }

  if (config.params && config.params.filter && config.params.filter.where) {
    config.params.filter.where = normalizeSchoolUserContactSearchWhere(config.params.filter.where);
  }

  return axios.get<Count>(`${window.apiBase}/i/schools/${activeSchoolId}/users/count`, config).then(response => {
    return response.data;
  });
}

export function assignUserTag(user: AppUser, data, userId) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/users/${userId}/userTags`, data, config)
    .then(response => {
      return response.data;
    });
}

export function deleteUserTagByUser(user: AppUser, userId, userTagId) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .delete(`${window.apiBase}/i/schools/${activeSchoolId}/users/${userId}/userTags/${userTagId}`, config)
    .then(response => {
      return response.data;
    });
}

export function updateUserTagsInBulk(user: AppUser, userIds: string[], userTagIds: string[], action: 'ADD' | 'REMOVE') {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/users/userTags/bulk`,
      { userIds, userTagIds, action },
      { headers: { usid: key } }
    )
    .then(response => response.data);
}

export interface AccountSetupReminderPreview {
  selectedCount: number;
  eligibleCount: number;
  skippedCount: number;
  audience: 'STUDENT' | 'STAFF' | 'PARENT' | 'MIXED';
  audiences: { students: number; staff: number; parents: number; mixed: number };
  skipped: { alreadySetUp: number; noEmail: number; inactive: number; noSchoolRole: number };
}

export function previewAccountSetupReminders(user: AppUser, userIds: string[]): Promise<AccountSetupReminderPreview> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/users/account-setup-reminders/preview`,
      { userIds },
      { headers: { usid: key } }
    )
    .then(response => response.data);
}

export function sendAccountSetupReminders(
  user: AppUser,
  userIds: string[],
  linkLifetimeHours: 24 | 48 | 72 | 168
): Promise<AccountSetupReminderPreview & { queuedCount: number }> {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/users/account-setup-reminders/send`,
      { userIds, linkLifetimeHours },
      { headers: { usid: key } }
    )
    .then(response => response.data);
}
